<template>
    <div class="card">
        <div class="table-responsive">
            <table class="table nowrap">
                <thead>
                    <tr>
                        <th>Invoice no</th>
                        <th>Invoice Date</th>
                        <th>Bill Type</th>
                        <th>Consultant</th>
                        <th>Created By</th>
                        <th>Patient ID</th>
                        <th>Patient Name</th>
                        <th>Patient Mobile No</th>
                        <th>Unit</th>
                        <th>Admission No</th>
                        <th>Referrer</th>
                        <th class="text-right">Amount</th>
                        <th class="text-right">VAT Amount</th>
                        <th class="text-right">Total Sales Amount</th>
                        <th class="text-right">Settlement Dis</th>
                        <th class="text-right">Total Amount</th>
                        <th class="text-right">Bill Sett. Discount</th>
                        <th class="text-right">Adjusted Amount</th>
                        <th class="text-right">Net Collection</th>
                        <th class="text-right">Due Amount</th>
                        <th class="text-right">Payment Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(child, childIndex) in props.sales" :key="childIndex">
                        <th>{{ child.bill_number }}</th>
                        <th>{{ child.date }}</th>
                        <th>{{ child.invoice_type }}</th>
                        <th>{{ child.service_resource && child.service_resource.name }}</th>
                        <th>{{ child.created_user.name }}</th>
                        <th>{{ child.contact.patient_id }}</th>
                        <th>{{ child.contact.full_name }}</th>
                        <th>{{ child.contact.mobile_no }}</th>
                        <th></th>
                        <th></th>
                        <th>{{ child.referrer_name }}</th>
                        <th class="text-right">{{ commaFormat(child.subtotal_amount) }}</th>
                        <th class="text-right">{{ commaFormat(child.vat_amount) }}</th>
                        <th class="text-right">{{ commaFormat(child.subtotal_amount + child.vat_amount) }}</th>
                        <th class="text-right">{{ totalDiscount(child) }}</th>
                        <th class="text-right">{{ commaFormat(child.subtotal_amount + child.vat_amount - totalDiscount(child)) }}</th>
                        <th class="text-right">{{ commaFormat(child.discount) }}</th>
                        <th class="text-right">{{ commaFormat(child.adjusted_amount) }}</th>
                        <th class="text-right">{{ commaFormat(child.paid_amount) }}</th>
                        <th class="text-right">{{ commaFormat((child.total_amount - child.paid_amount - child.discount - child.adjusted_amount)) }}</th>
                        <th class="text-center">{{ child.payment_status }}</th>
                    </tr>
                    <tr>
                        <th colspan="11" class="text-right">Total ({{ props.sales.length }})</th>
                        <th class="text-right">{{ commaFormat(getTotalAmount(props.sales)) }}</th>
                        <th class="text-right">{{ commaFormat(getTotalVat(props.sales)) }}</th>
                        <th class="text-right">{{ commaFormat(getTotalSalesAmount(props.sales)) }}</th>
                        <th class="text-right">{{ commaFormat(getTotalDiscount(props.sales)) }}</th>
                        <th class="text-right">{{ commaFormat(getTotalNetBillAmount(props.sales)) }}</th>
                        <th class="text-right">{{ commaFormat(getTotalDicountAmount(props.sales)) }}</th>
                        <th class="text-right">{{ commaFormat(getTotalAdjustedAmount(props.sales)) }}</th>
                        <th class="text-right">{{ commaFormat(getTotalPaidAmount(props.sales)) }}</th>
                        <th class="text-right">{{ commaFormat(getTotalDue(props.sales)) }}</th>
                    </tr>
                    <tr v-if="grandTotal">
                        <th colspan="11" class="text-right">Grand Total</th>
                        <th class="text-right">{{ commaFormat(grandTotal.subtotal) }}</th>
                        <th class="text-right">{{ commaFormat(grandTotal.vat) }}</th>
                        <th class="text-right">{{ commaFormat(grandTotal.sales) }}</th>
                        <th class="text-right">{{ commaFormat(grandTotal.settlementDiscount) }}</th>
                        <th class="text-right">{{ commaFormat(grandTotal.netBill) }}</th>
                        <th class="text-right">{{ commaFormat(grandTotal.discount) }}</th>
                        <th class="text-right">{{ commaFormat(grandTotal.adjusted) }}</th>
                        <th class="text-right">{{ commaFormat(grandTotal.paid) }}</th>
                        <th class="text-right">{{ commaFormat(grandTotal.due) }}</th>
                    </tr>
                </tbody>
            </table>
          <p v-if="!props.sales.length" class="text-center mt-5">No data</p>
        </div>
    </div>
</template>

<script setup>
import figureFormatter from "@/services/utils/figureFormatter";
import { computed } from "vue";

const props = defineProps({
    sales: Array,
    summary: { type: Object, default: null }
})

const { commaFormat } = figureFormatter();

const grandTotal = computed(() => {
  if (!props.summary) return null
  const subtotal = parseFloat(props.summary.subtotal_amount) || 0
  const vat = parseFloat(props.summary.vat_amount) || 0
  const totalDiscount = parseFloat(props.summary.total_discount) || 0
  const total = parseFloat(props.summary.total_amount) || 0
  const paid = parseFloat(props.summary.paid_amount) || 0
  const adjusted = parseFloat(props.summary.adjusted_amount) || 0
  const discount = parseFloat(props.summary.discount) || 0
  return {
    subtotal,
    vat,
    sales: subtotal + vat,
    settlementDiscount: totalDiscount + discount,
    netBill: subtotal + vat - totalDiscount - discount,
    discount,
    adjusted,
    paid,
    due: total - paid - discount - adjusted
  }
})

const getTotalAmount = (item) => {
    return item.reduce((total, saleMaster) => total + saleMaster.subtotal_amount, 0);
}

const totalDiscount = (invoice) => {
  let  totalDiscount = invoice.receipt_generals.reduce((sum, item) => sum + (item.discount_amount ?? 0), 0);
  totalDiscount += invoice.total_discount;
  return commaFormat(totalDiscount.toFixed(2))
}

const getTotalVat = (item) => {
    return item.reduce((total, saleMaster) => total + saleMaster.vat_amount, 0);
}

const getTotalSalesAmount = (item) => {
    return item.reduce((total, saleMaster) => total + (saleMaster.subtotal_amount + saleMaster.vat_amount), 0);
}

const getTotalDiscount = (item) => {
    let saleMasterDiscount =  item.reduce((total, saleMaster) => total + saleMaster.total_discount, 0);
    let receiptGeneralDiscount = item.reduce((total, saleMaster) => {
      return  total += saleMaster.receipt_generals.reduce((sum, receipt) => sum + receipt.discount_amount, 0);
    }, 0);

    return saleMasterDiscount + receiptGeneralDiscount;
}

const getTotalNetBillAmount = (item) => {
    let totalNetAmount =  item.reduce((total, saleMaster) => total + (saleMaster.subtotal_amount + saleMaster.vat_amount), 0);
    return totalNetAmount - getTotalDiscount(item);
}

const getTotalPaidAmount = (item) => {
    return item.reduce((total, saleMaster) => total + saleMaster.paid_amount, 0);
}
const getTotalDicountAmount = (item) => {
    return item.reduce((total, saleMaster) => total + saleMaster.discount, 0);
}
const getTotalResourceCostAmount = (item) => {
    return item.reduce((total, saleMaster) => total + saleMaster.service_resource_cost, 0);
}

const getTotalAdjustedAmount = (item) => {
    return item.reduce((total, saleMaster) => total + saleMaster.adjusted_amount, 0);
}

function getTotalDue(items) {
  return items.reduce((sum, child) => {
    const total = parseFloat(child.total_amount) || 0
    const paid = parseFloat(child.paid_amount) || 0
    const adjusted = parseFloat(child.adjusted_amount) || 0;
    const discount = parseFloat(child.discount);

    const due = total - discount - paid - adjusted
    return sum + due
  }, 0)
}
</script>

<style scoped>
    .nowrap{
        white-space: nowrap;
    }
</style>
